import { ReactNode } from 'react';

/** Props definition for a table component. */
export type TableProps<T> = {
  activeRow: (row: T) => boolean;
  children: (column: string, row: T) => ReactNode;
  columns: (keyof T & string)[];
  columnClasses: string[];
  columnLabels: string[];
  data: T[];
  onBlur?: () => void;
  onHover?: (row: T) => void;
};

/** Tables display sets of data. */
export function Table<T>({
  activeRow,
  children,
  columns,
  columnClasses,
  columnLabels,
  data,
  onBlur,
  onHover,
}: TableProps<T>) {
  return (
    <div className="p-2 flex flex-col cursor-default">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {Array.from(columns.keys()).map((index) => (
                    <th
                      key={index}
                      scope="col"
                      className={`${columnClasses[index]} px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider`}
                    >
                      {columnLabels[index]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((row, index) => (
                  <tr
                    key={index}
                    className={`${activeRow(row) ? '' : 'md:opacity-30'} transition-opacity`}
                    onMouseEnter={() => onHover && onHover(row)}
                    onMouseLeave={() => onBlur && onBlur()}
                  >
                    {columns.map((column, index) => (
                      <td
                        key={index}
                        className={`${columnClasses[index]} px-6 py-4 whitespace-nowrap text-sm md:text-base`}
                      >
                        {children(column, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
